import type { JSX } from 'react';
import { Fragment } from 'react';
import type { CustomControlCode } from '@common/enums';
import type { Field } from '@common/formEngine/formEngine.types';
import { isDependsOn } from '@common/formEngine/dependsOn';
import { isContentField } from '@common/formEngine/typedSections';
import { deepPick } from '@common/utils/deepPickUtils';
import { nonNullable } from '@common/utils/nonNullableTypeUtils';
import { useEditFormContext } from '@frontend/formEngine/editForm/useEditFormContext';
import { useProcessActivityEditContext } from '@frontend/utils';
import type { EditFormCommonProps } from './editForm.types';
import { EditFormSectionInputField } from './EditFormSectionInputField';
import { EditFormSectionInputFieldContextProvider } from './EditFormSectionInputFieldContextProvider';

type EditFormSectionFieldProps<
    Data extends Record<string, unknown>,
    ContentKey extends string | undefined,
    TCustomControlCode extends CustomControlCode | undefined,
    Version extends number,
> = EditFormCommonProps<Data, ContentKey> & {
    readonly field: Field<Data, ContentKey, TCustomControlCode, Version>;
};

export function EditFormSectionField<
    Data extends Record<string, unknown>,
    ContentKey extends string | undefined,
    TCustomControlCode extends CustomControlCode | undefined,
    Version extends number,
>({
    contentComponents,
    inputComponents,
    field,
}: EditFormSectionFieldProps<Data, ContentKey, TCustomControlCode, Version>): JSX.Element | undefined {
    const { processActivityContextData } = useProcessActivityEditContext<Data, TCustomControlCode, Version>();
    const { onChange } = useEditFormContext<Data, TCustomControlCode, Version>();

    if (isContentField(field)) {
        const { key } = field;

        return (
            <Fragment key={key}>
                {nonNullable(
                    contentComponents?.get(key as ContentKey),
                    `Expected to have content component for key ${key}`
                )}
            </Fragment>
        );
    }

    const { key, isRequired } = field;
    const evaluatedIsRequired = isDependsOn<Data, boolean, TCustomControlCode, Version>(isRequired)
        ? isRequired.fn(deepPick(processActivityContextData, isRequired.fieldKeys))
        : isRequired;

    return (
        <EditFormSectionInputFieldContextProvider
            field={field}
            isRequired={evaluatedIsRequired}
            key={'category' in field ? `${key}-${field.category}` : key}
            onChange={(value: unknown) => onChange(value, field)}
        >
            {inputComponents?.has(key) ? inputComponents.get(key) : <EditFormSectionInputField />}
        </EditFormSectionInputFieldContextProvider>
    );
}
